import { useQuery } from '@tanstack/react-query';
import type { TerritoryDto } from '@rw/shared';
import { useMemo } from 'react';
import { qk } from '../../hooks/queries';
import { api } from '../../lib/api';
import type { LoadedRegion } from '../../services/runtime';
import { useTerritoryLive } from '../../stores/territory-live';

/** All owned territories of a region (≤ a few thousand rows), merged with realtime updates. */
export function useOwnedTerritories(region: LoadedRegion | null) {
  const q = useQuery({
    queryKey: qk.owned(region?.dto.id ?? ''),
    enabled: !!region,
    staleTime: 30_000,
    queryFn: async () => {
      const { minLat, maxLat, minLng, maxLng } = region!.bbox;
      const rows = await api<TerritoryDto[]>(`/regions/${region!.dto.id}/territories`, { query: { minLat, maxLat, minLng, maxLng } });
      useTerritoryLive.getState().reset();
      return rows;
    },
  });
  const overrides = useTerritoryLive((s) => s.overrides);
  const owners = useMemo(() => {
    const m = new Map<string, TerritoryDto>();
    for (const t of q.data ?? []) m.set(t.gridKey, t);
    for (const t of Object.values(overrides)) m.set(t.gridKey, t);
    return m;
  }, [q.data, overrides]);
  return { owners, query: q };
}
